/**
 * Generic CSV export util, driven by a declarative "sections" template so
 * any page can describe what it wants (key/value summary rows, a
 * label/value block, a table with merged group headers, …) without each
 * page reimplementing CSV-string building + file download.
 *
 * Usage:
 *   exportCsv({
 *     filename: "report.csv",
 *     sections: [
 *       { type: "keyValueRow", label: "Filters", items: [{ label: "Status", value: "All" }] },
 *       { type: "spacer" },
 *       { type: "labelValueRows", items: [{ label: "No. of Class", value: 12 }] },
 *       { type: "spacer" },
 *       {
 *         type: "table",
 *         columns: [
 *           { label: "No" },
 *           { path: ["Schedule"], label: "EC in charge", value: (row) => row.csoInCharge },
 *         ],
 *         rows: data,
 *       },
 *     ],
 *   })
 */

// Escapes a value for one CSV field per RFC 4180: wrap in quotes if it
// contains a comma, quote or newline, doubling any embedded quotes.
export const csvCell = (value) => {
  const s = value === null || value === undefined ? "" : String(value)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

const buildKeyValueRow = (section) => {
  const parts = (section.items ?? []).map(
    (item) => `${item.label}: ${item.value ?? "-"}`
  )
  const cells = section.label ? [section.label, ...parts] : parts
  return [cells]
}

const buildLabelValueRows = (section) => {
  const items = section.items ?? []
  return [items.map((item) => item.label), items.map((item) => item.value ?? "-")]
}

/**
 * Builds the header rows for a "table" section. Columns may declare a
 * `path` (e.g. ["Class Situation (Week)", "This week"]) representing the
 * group/sub-group they belong to. Consecutive leaf columns that share the
 * same path segment are merged visually by only printing the label on the
 * first leaf of the span and leaving the rest blank on that header row —
 * mirroring a merged/rowspan header without needing real cell merges.
 */
const buildTableHeaderRows = (columns) => {
  const depth = columns.reduce((max, col) => Math.max(max, col.path?.length ?? 0), 0)
  const headerRows = []

  for (let level = 0; level < depth; level += 1) {
    let prevSegment
    const row = columns.map((col) => {
      const segment = col.path?.[level]
      if (segment === undefined) return ""
      const isSameSpanAsPrev = segment === prevSegment
      prevSegment = segment
      return isSameSpanAsPrev ? "" : segment
    })
    headerRows.push(row)
  }

  headerRows.push(columns.map((col) => col.label))
  return headerRows
}

const buildTableRows = (section) => {
  const columns = section.columns ?? []
  const rows = section.rows ?? []
  return [
    ...buildTableHeaderRows(columns),
    ...rows.map((row) => columns.map((col) => col.value?.(row) ?? "-")),
  ]
}

/**
 * Converts a list of section descriptors into a CSV matrix (string[][]).
 */
export const buildCsvMatrix = (sections = []) => {
  const matrix = []
  sections.forEach((section) => {
    switch (section.type) {
      case "spacer":
        matrix.push([])
        break
      case "keyValueRow":
        matrix.push(...buildKeyValueRow(section))
        break
      case "labelValueRows":
        matrix.push(...buildLabelValueRows(section))
        break
      case "table":
        matrix.push(...buildTableRows(section))
        break
      default:
        break
    }
  })
  return matrix
}

const matrixToCsvText = (matrix) =>
  matrix.map((row) => row.map(csvCell).join(",")).join("\n")

const downloadCsvText = (filename, csvText) => {
  const blob = new Blob([`﻿${csvText}`], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Builds the CSV from `sections` and triggers a client-side file download.
 */
export const exportCsv = ({ filename = "export.csv", sections = [] }) => {
  const matrix = buildCsvMatrix(sections)
  const csvText = matrixToCsvText(matrix)
  downloadCsvText(filename, csvText)
}
